import { type FC } from "react";

import clsx from "clsx";

import { IntelligencePanelText } from "./intelligence_panel";
import { PRACTICE_CHIPS, SEGMENT_COPY, type Segment } from "./segment_copy";

const WAVE_BARS = [
  { height: 12 },
  { height: 22, animationDelay: ".12s" },
  { height: 28, animationDelay: ".24s" },
  { height: 18, animationDelay: ".36s" },
  { height: 26, animationDelay: ".48s" },
  { height: 14, animationDelay: ".6s" },
];

const Connector: FC<{ lane: "l1" | "l2" }> = ({ lane }) => (
  <div className={clsx("link", lane)}>
    <svg viewBox="0 0 44 16">
      <path d="M2,8 L34,8" />
      <polygon points="34,3 43,8 34,13" />
    </svg>
  </div>
);

// Markup transcribed from the design port kit (sections/Intelligences.html).
// The three panel interiors are constructed graphics — HTML elements animated by
// home.css, not images and not SVG. Do not rebuild them with utilities.
export const Intelligence: FC<{
  copySegment: Segment;
  fading: boolean;
  washing: boolean;
  onWashEnd: () => void;
}> = ({ copySegment, fading, washing, onWashEnd }) => {
  const copyState = { copySegment, fading, washing, onWashEnd };
  const hidesDictation = SEGMENT_COPY[copySegment].hidesDictation;

  return (
    <section className="pad" id="intel">
      <div className="wrap">
        <div
          className={clsx("art", hidesDictation && "nodict")}
          aria-label="One case flowing through Record, Dictation and Report Intelligence"
        >
          <div className="panel p1">
            <IntelligencePanelText
              who="Record Intelligence"
              verb="Work faster. Think better."
              field="record"
              {...copyState}
            />
            <div className="mini" style={{ paddingBottom: 36 }}>
              <div className="qrow">
                <span className="mag"></span>
                <span className="qtxt">
                  Were any psychiatric medications used?
                </span>
              </div>
              <div className="srch">
                <span className="spin1"></span>Searching the records
                <span className="kw">“psychiatric”</span>
                <span className="kw">“medication”</span>
                <span className="kw">“antidepressant”</span>
                <span className="kwmore">+6 more</span>
              </div>
              <div className="rrow1 a1">
                <span className="fd">08/10/05</span>
                <span className="ft">
                  <b>Temazepam 15 mg</b> — discontinued
                </span>
                <span className="fc">L. 278</span>
              </div>
              <div className="rrow1 a2">
                <span className="fd">10/15/19</span>
                <span className="ft">
                  <b>Sertraline 50 mg</b> — started
                </span>
                <span className="fc">p. 1,203</span>
              </div>
              <div className="chip1">Every finding cited to the page</div>
            </div>
          </div>

          <Connector lane="l1" />

          <div
            className={clsx("panel p2", hidesDictation && "dimcard")}
            id="card-dictation"
          >
            <IntelligencePanelText
              who="Dictation Intelligence"
              verb="Write better."
              field="dictation"
              {...copyState}
            />
            <div className="mini">
              <div className="dgrid">
                <div className="micside">
                  <div className="mic">
                    <span className="cap2"></span>
                    <span className="arc"></span>
                    <span className="stem"></span>
                  </div>
                  <div className="wave">
                    {WAVE_BARS.map((bar) => (
                      <span key={bar.height} className="wb" style={bar}></span>
                    ))}
                  </div>
                </div>
                <div className="txtside">
                  <div>
                    <div className="dlab">You said</div>
                    <div className="raw">
                      “<s>Uh</s> okay, this is <s>uh</s> Friday, April 18th…{" "}
                      <s>um</s> evaluation with Mr. Smith…”
                    </div>
                  </div>
                  <div>
                    <div className="dlab g">Your notes</div>
                    <div className="cleaned">
                      <span className="rv">
                        On April 18, 2025, the applicant appeared
                      </span>
                      <span className="rv r2">
                        for evaluation with Spanish interpretation.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Connector lane="l2" />

          <div className="panel p3">
            <IntelligencePanelText
              who="Report Intelligence"
              verb="Defend better."
              field="report"
              {...copyState}
            />
            <div className="mini" style={{ paddingBottom: 34 }}>
              <div className="rrow">
                <div className="rl">
                  Causation opinion &gt;50% stated <small>— p. 17</small>
                </div>
                <div className="mk m1">
                  <span className="ok">✓</span>
                </div>
              </div>
              <div className="rrow">
                <div className="rl gaptxt">
                  Signature block incomplete <small>— p. 66</small>
                </div>
                <div className="mk g1">
                  <span className="gap">!</span>
                </div>
              </div>
              <div className="rrow">
                <div className="rl">
                  GAF consistent with testing <small>— p. 13</small>
                </div>
                <div className="mk m2">
                  <span className="ok">✓</span>
                </div>
              </div>
              <div className="rrow">
                <div className="rl gaptxt">
                  Medications section <small>— p. 8 · fixed</small>
                </div>
                <div className="mk fix">
                  <span className="fl">!</span>
                  <span className="ok2">✓</span>
                </div>
              </div>
              <div className="sumchip">
                <b className="cGreen">✓ 13 compliant</b>
                <b className="cAmber">3 critical · 1 important</b>
              </div>
            </div>
          </div>

          <div
            className={clsx("pmband", copySegment === "pm" && "on")}
            id="pmband"
            style={copySegment === "pm" ? undefined : { display: "none" }}
          >
            {PRACTICE_CHIPS.map((chip, index) => (
              <span key={chip} className={`pmchip c${index + 1}`}>
                {chip}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
